// Блюда ABC: поиск, чип активного фильтра (рекомендация, группа или клетка матрицы), сортировка, первые 30.
// Десктоп — таблица с закреплённым под шапкой приложения заголовком и сортировкой по клику на колонку;
// телефон — карточки и кнопка «Сортировка». Что показывать — abcList.visibleDishes.
import React, { useState } from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DESKTOP_HEADER_HEIGHT } from '../../../../components/layout/AppLayout';
import { CARD_BG_TOP, CARD_LABEL_COLOR, CARD_SURFACE_SX, DIVIDER_COLOR, MUTED_TEXT_COLOR, SECTION_ACCENTS } from '../../../../styles/tokens';
import { ABC_TAGS } from '../../utils/abcCalculations';
import { SORT_FIELDS, filterLabel, toggleSort, visibleDishes } from '../../utils/abcList';
import { AbcLetters, AbcLettersHeader, LETTER_SLOT, TAG_COLORS, money, plural, quantity } from './abcLook';
import { ChangeMark } from './AbcChanges';

const ACCENT = SECTION_ACCENTS['/cost'];
const NO_COST_HINT = 'Нет себестоимости — прибыль завышена';
const NUMERIC = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const focusSx = { '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' } };
const textButtonSx = { minHeight: 44, px: 1.5, borderRadius: '8px', fontSize: '14px', fontWeight: 600, color: ACCENT.tab, ...focusSx };

function NoCostMark() {
  return (
    <Tooltip title={NO_COST_HINT}>
      <WarningAmberRoundedIcon aria-label={NO_COST_HINT} sx={{ fontSize: 18, color: TAG_COLORS.check, flexShrink: 0 }} />
    </Tooltip>
  );
}

// Разные блюда с одним названием (свои товары в ресторанах сети) — подпись с категорией, чтобы их различать
function DuplicateHint({ dish }) {
  if (!dish.duplicateName) return null;
  return <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR, fontWeight: 400 }}>{dish.category}</Typography>;
}

function SearchField({ value, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 44, px: 1.25, borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.06)', flex: { xs: '1 1 100%', md: '0 1 300px' } }}>
      <SearchIcon fontSize="small" sx={{ color: MUTED_TEXT_COLOR }} aria-hidden="true" />
      <InputBase
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Поиск блюда"
        inputProps={{ 'aria-label': 'Поиск блюда по названию' }}
        sx={{ flex: 1, color: '#fff', fontSize: '14px' }}
      />
      {value && (
        <IconButton size="small" onClick={() => onChange('')} aria-label="Очистить поиск" sx={{ color: MUTED_TEXT_COLOR, width: 36, height: 36 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function FilterChip({ filter, onClear }) {
  if (!filter) return null;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, minHeight: 36, pl: 1.5, pr: 0.25, borderRadius: '18px', backgroundColor: `rgba(${ACCENT.rgb},0.18)` }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>{filterLabel(filter)}</Typography>
      <IconButton size="small" onClick={onClear} aria-label="Снять фильтр" sx={{ color: '#fff', width: 36, height: 36 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// Телефон: «Сортировка: Прибыль ↓», повторный выбор того же — другое направление
function SortPicker({ sort, onSort }) {
  const [anchor, setAnchor] = useState(null);
  const arrow = sort.dir === 'desc' ? '↓' : '↑';
  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        sx={{ ...textButtonSx, color: '#fff', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '10px' }}
      >
        <Box component="span" sx={{ color: MUTED_TEXT_COLOR, fontWeight: 500, mr: 0.5 }}>Сортировка:</Box>
        {`${SORT_FIELDS[sort.field]} ${arrow}`}
      </ButtonBase>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {Object.entries(SORT_FIELDS).map(([field, label]) => (
          <MenuItem
            key={field}
            selected={sort.field === field}
            onClick={() => {
              onSort(toggleSort(sort, field));
              setAnchor(null);
            }}
            sx={{ minHeight: 44 }}
          >
            {sort.field === field ? `${label} ${arrow}` : label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function Excluded({ count }) {
  if (!count) return null;
  return (
    <Typography sx={{ mt: 1, fontSize: '13px', color: MUTED_TEXT_COLOR }}>
      {`Не учтено ${count} ${plural(count, ['позиция', 'позиции', 'позиций'])} с нулевой или отрицательной выручкой`}
    </Typography>
  );
}

const headSx = {
  color: MUTED_TEXT_COLOR,
  fontSize: '12px',
  fontWeight: 500,
  borderBottom: `1px solid ${DIVIDER_COLOR}`,
  py: 1,
  // Закреплён под шапкой приложения (на десктопе закреплена только она); у таблицы нет своей прокрутки
  position: 'sticky',
  top: DESKTOP_HEADER_HEIGHT,
  zIndex: 2,
  backgroundColor: CARD_BG_TOP,
};
const cellSx = { color: CARD_LABEL_COLOR, fontSize: '14px', borderBottom: `1px solid ${DIVIDER_COLOR}`, py: 1 };

function SortHeader({ field, sort, onSort, width }) {
  const active = sort.field === field;
  return (
    <TableCell sx={{ ...headSx, ...NUMERIC, width }} aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
      <TableSortLabel
        active={active}
        direction={active ? sort.dir : 'desc'}
        onClick={() => onSort(toggleSort(sort, field))}
        sx={{
          color: 'inherit',
          '&.Mui-active': { color: '#fff' },
          '&:hover': { color: '#fff' },
          '& .MuiTableSortLabel-icon': { color: `${ACCENT.tab} !important` },
          ...focusSx,
        }}
      >
        {SORT_FIELDS[field]}
      </TableSortLabel>
    </TableCell>
  );
}

function DishTable({ items, sort, onSort }) {
  return (
    <Box sx={CARD_SURFACE_SX}>
      <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Блюдо</TableCell>
            <SortHeader field="qty" sort={sort} onSort={onSort} width={104} />
            <SortHeader field="revenue" sort={sort} onSort={onSort} width={118} />
            <SortHeader field="profit" sort={sort} onSort={onSort} width={118} />
            <SortHeader field="profitPerPortion" sort={sort} onSort={onSort} width={150} />
            <TableCell sx={{ ...headSx, width: LETTER_SLOT * 3 + 32, whiteSpace: 'nowrap' }}><AbcLettersHeader /></TableCell>
            <TableCell sx={{ ...headSx, width: 164 }}>Вывод</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((dish) => (
            <TableRow key={dish.key} sx={{ '&:last-of-type td': { borderBottom: 'none' } }}>
              <TableCell sx={{ ...cellSx, color: '#fff', fontWeight: 600 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  {/* До двух строк, дальше многоточие; полное название — в подсказке */}
                  <Box
                    component="span"
                    title={dish.name}
                    sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.35 }}
                  >
                    {dish.name}
                  </Box>
                  {dish.noCost && <NoCostMark />}
                  <ChangeMark change={dish.change} letter={dish.abc.profit} />
                </Box>
                <DuplicateHint dish={dish} />
              </TableCell>
              <TableCell sx={{ ...cellSx, ...NUMERIC }}>{quantity(dish.qty)}</TableCell>
              <TableCell sx={{ ...cellSx, ...NUMERIC }}>{money(dish.revenue)}</TableCell>
              <TableCell sx={{ ...cellSx, ...NUMERIC, color: '#fff' }}>{money(dish.profit)}</TableCell>
              <TableCell sx={{ ...cellSx, ...NUMERIC }}>{money(dish.profitPerPortion)}</TableCell>
              <TableCell sx={cellSx}><AbcLetters abc={dish.abc} /></TableCell>
              <TableCell sx={{ ...cellSx, color: TAG_COLORS[dish.tag], fontWeight: 600 }}>{ABC_TAGS[dish.tag].label}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function DishCards({ items }) {
  return (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((dish) => (
        <Box component="li" key={dish.key} sx={{ ...CARD_SURFACE_SX, px: 1.75, py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: '15px', fontWeight: 600, color: '#fff', wordBreak: 'break-word' }}>
              {dish.name}
            </Typography>
            {dish.noCost && <NoCostMark />}
            <ChangeMark change={dish.change} letter={dish.abc.profit} />
            <AbcLetters abc={dish.abc} />
          </Box>
          <DuplicateHint dish={dish} />
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
            <Typography sx={{ fontSize: '13px', color: CARD_LABEL_COLOR, fontVariantNumeric: 'tabular-nums' }}>
              {`${money(dish.profit)} прибыли, ${quantity(dish.qty)} шт`}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: TAG_COLORS[dish.tag], whiteSpace: 'nowrap' }}>
              {ABC_TAGS[dish.tag].label}
            </Typography>
          </Box>
          {dish.noCost && <Typography sx={{ mt: 0.25, fontSize: '12px', color: TAG_COLORS.check }}>{NO_COST_HINT}</Typography>}
          {dish.change?.notable && dish.change.reason?.text && (
            <Typography sx={{ mt: 0.25, fontSize: '12px', color: MUTED_TEXT_COLOR }}>{dish.change.reason.text}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function AbcDishes({ dishes, filter, onFilter, query, onQuery, sort, onSort, showAll, onShowAll, excluded, desktop }) {
  const { items, total, hidden } = visibleDishes(dishes, { filter, query, sort, showAll });
  const narrowed = Boolean(filter) || Boolean(query.trim());
  const reset = () => {
    onQuery('');
    onFilter(null);
  };

  return (
    <Box component="section" aria-label="Блюда">
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
        <Typography component="h3" sx={{ fontSize: '15px', fontWeight: 700, color: '#fff', mr: 1 }}>
          {narrowed ? `Блюда · ${total} из ${dishes.length}` : `Блюда · ${dishes.length}`}
        </Typography>
        <FilterChip filter={filter} onClear={() => onFilter(null)} />
        <Box sx={{ flex: { md: 1 } }} />
        <SearchField value={query} onChange={onQuery} />
        {!desktop && <SortPicker sort={sort} onSort={onSort} />}
      </Box>

      {total === 0 ? (
        <Box role="status" sx={{ ...CARD_SURFACE_SX, px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>Ничего не найдено</Typography>
          <ButtonBase onClick={reset} sx={textButtonSx}>Сбросить</ButtonBase>
        </Box>
      ) : desktop ? (
        <DishTable items={items} sort={sort} onSort={onSort} />
      ) : (
        <DishCards items={items} />
      )}

      {hidden > 0 && (
        <ButtonBase onClick={onShowAll} sx={{ ...textButtonSx, mt: 1, width: { xs: '100%', md: 'auto' }, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '10px' }}>
          {`Показать все ${total}`}
        </ButtonBase>
      )}
      <Excluded count={excluded} />
    </Box>
  );
}
